import * as Yup from 'yup';
import moment from "moment-timezone";
import { IReward } from "../../models/Reward";
import { IUserSessionStore } from "../../models/UserSessionStore";
import { LanguageService } from "../../utils/languageService";

const NEW_REWARD_ID_PREFIX = "NEW__1";

/**
 * Reward form validation schema
 */
export const getRewardValidationSchema = (t: Function, languageService: LanguageService) => {
  return Yup.object().shape({
    id: Yup.string().required(),
    title: languageService.createLocalizedContentSchema(),
    message: Yup.string().required(t("fieldRequired")),
    description: languageService.createLocalizedContentSchema(),
    timing_rules: Yup.object({
      validity: Yup.object({ 
        duration: Yup.number().required(t("fieldRequired")),
        durationUnit: Yup.string().required(t("fieldRequired"))
      }),
    }),
    images: Yup.object({           
      mainImage: Yup.string().nullable(),
    }),
    assigned_venues: Yup.array().of(Yup.string()),
    active: Yup.boolean().required(t("fieldRequired"))
  });
};

/**
 * Default values for creating a new Reward
 */
export const getRewardDefaultValues = (userSessionStore: IUserSessionStore, languageService: LanguageService) => ({
  id: NEW_REWARD_ID_PREFIX,
  description: languageService.createLocalizedContentDefaults(),
  timing_rules: {
    validity: { 
      duration: 30,
      durationUnit: "day" 
    }
  },
  message: "",
  title: languageService.createLocalizedContentDefaults(),
  images: {           
    mainImage: null
  },
  assigned_venues: userSessionStore?.isOrganizationContext() ? [] : [
    ...(userSessionStore?.selectedBranch?.id ? [userSessionStore?.selectedBranch?.id] : [])
  ],
  active: true
});

/**
 * Transform a Reward model instance to form values
 */
export const transformRewardValues = (reward: IReward, contentLanguages: string[]) => {
  return {
    id: reward.id,
    description: {
      ...Object.fromEntries(
        contentLanguages.map((langCode: string) => [
          langCode,
          reward.description.get(langCode) || ""
        ])
      )
    },
    message: reward.message || "",
    timing_rules: {
      validity: { 
        duration: reward.timing_rules?.validity?.duration || 30,
        durationUnit: reward.timing_rules?.validity?.durationUnit || "day" 
      }
    },
    title: {
      ...Object.fromEntries(
        contentLanguages.map((langCode: string) => [
          langCode,
          reward.title.get(langCode) || ""
        ])
      )
    },
    images: {           
      mainImage: reward.images?.mainImage || null
    },
    assigned_venues: reward.assigned_venues?.map((venue: any) => venue.id) || [],
    active: Boolean(reward.active),
  };
};

/**
 * Prepare reward data for submission
 */
export const prepareRewardForSubmission = (values: any, rewardTriggerId: string) => {
  return {
    ...values,
    reward_trigger_id: rewardTriggerId,
  };
};

export { NEW_REWARD_ID_PREFIX }; 